import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WagmiWalletContext';
import { useContracts } from '../context/ContractContext';
import { useFunctionExplorerState } from '../context/TabStateContext';
import { parseComplexResult } from '../../utils/complexResultParser';
import BurstDataDisplay from '../common/BurstDataDisplay';
import {
    Box,
    Paper,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Button,
    Alert,
    Tooltip,
    Chip,
    useTheme,
    Grid2,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Fab
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    AccountBalance as ContractIcon,
    Functions as FunctionsIcon,
    Build as UtilityIcon,
    Edit as SignatureIcon,
    PlayArrow as ExecuteIcon,
    Visibility as ViewIcon,
    Create as WriteIcon,
    Security as SecurityIcon,
    AccountTree as WorkflowIcon,
    Close as CloseIcon,
    SwapHoriz as ConvertIcon
} from '@mui/icons-material';
import MultiSigWalletSignatureFlow from './MultiSigWalletSignatureFlow';
import { useSignatureContext } from '../context/SignatureContext';
import SaltGenerator from './SaltGenerator';
import DelayHelper from './DelayHelper';
import SmartHexDisplay from '../utils/SmartHexDisplay';
import ErrorDisplay from '../common/ErrorDisplay';
import SecureDataViewer from '../common/SecureDataViewer';
import ContractDropdown from '../common/ContractDropdown';
import AddressSelector from '../common/AddressSelector';
import WorkflowShortcuts from '../workflow/WorkflowShortcuts';
import WeiInputConverter from '../utils/WeiInputConverter';
import { WorkflowTemplate } from '../../utils/workflowTemplates';
import workflowStorageService, { TemplateData } from '../../services/workflowStorageService';
import { getSmartDefault, validateParameterValue } from '../../utils/smartDefaults';
import { useFloatingConverter } from '../../hooks/useFloatingConverter';
import backendDataHandler from '../../services/backendDataHandler';
import workflowCompletionService, { WorkflowCompletionData, ScheduleData } from '../../services/workflowCompletionService';
import logger from '../../utils/logger';

interface FunctionExplorerProps {
    workflowContext: {
        activeWorkflows: { type: string; key: string; timestamp: number }[];
        registerWorkflow: (type: string) => string;
        completeWorkflow: (key: string) => void;
    };
    onTransactionComplete?: (transactionHash: string, receipt: any, executionContext: any) => void;
}

const FunctionExplorer: React.FC<FunctionExplorerProps> = ({ workflowContext, onTransactionComplete }) => {
    // Use our context hooks instead of useWeb3React
    const { provider, signer, isConnected } = useWallet();
    const { contracts, protocolAddresses } = useContracts();
    const theme = useTheme();

    // Use persistent tab state
    const { state: tabState, setState: setTabState } = useFunctionExplorerState();

    // Clean up stale signature flow state and error messages on component mount
    useEffect(() => {
        const cleanupStaleSignatureState = () => {
            try {
                // 🔄 CRITICAL: Don't cleanup if we just recovered from Coinbase reload
                const justRecovered = localStorage.getItem('just_recovered') === 'true';

                if (justRecovered) {
                    logger.log('🔄 RECOVERY: Skipping cleanup - state was just recovered from backup');
                    return; // Don't clean up recovered state
                }

                const isSignatureFlowActive = localStorage.getItem('signature_flow_active');
                const hasStaleSignatureState = tabState.showSignatureFlow;

                if (isSignatureFlowActive || hasStaleSignatureState) {
                    logger.log('🧹 Cleaning up stale signature flow state');
                    localStorage.removeItem('signature_flow_active');
                    localStorage.removeItem('function_inputs');

                    if (hasStaleSignatureState) {
                        setTabState({ showSignatureFlow: false });
                    }

                    if (tabState.executionStatus?.includes('Signature collection flow initiated')) {
                        setTabState({ executionStatus: '' });
                    }
                }

                // Clear stale error messages from previous workflow failures
                if (tabState.executionStatus) {
                    const staleErrorPatterns = [
                        'Transaction was rejected by the smart contract',
                        'execution reverted',
                        'Error: ',
                        'Failed to retrieve',
                        'Backend retrieval failed',
                        'Cannot execute transaction'
                    ];

                    const hasStaleError = staleErrorPatterns.some(pattern =>
                        tabState.executionStatus?.includes(pattern)
                    );

                    if (hasStaleError) {
                        setTabState({ executionStatus: '' });
                    }
                }
            } catch (error) {
                logger.error('Error cleaning up stale signature state:', error);
            }
        };

        cleanupStaleSignatureState();
    }, []);

    // Add recovery event listener to restore signature flow state
    useEffect(() => {
        const handleRecovery = () => {
            try {
                logger.log('🔄 Recovery event received - attempting to restore signature flow state');

                // Check if there's signature flow data to recover
                const storedData = localStorage.getItem('function_inputs');
                const isSignatureFlowActive = localStorage.getItem('signature_flow_active') === 'true';

                if (storedData && isSignatureFlowActive) {
                    const parsedData = JSON.parse(storedData);
                    logger.log('✅ Found signature flow data to recover:', {
                        functionName: parsedData.functionName,
                        contractAddress: parsedData.contractAddress
                    });

                    // Restore the signature flow state
                    setShowSignatureFlow(true);

                    // If we have contract and function info, restore those too
                    if (parsedData.contractAddress && contracts.length > 0) {
                        const contract = contracts.find(c => c.address === parsedData.contractAddress);
                        if (contract) {
                            setSelectedContract(contract);
                            setSelectedFunction(parsedData.functionName);
                            logger.log('✅ Restored contract and function selection');
                        }
                    }

                    setExecutionStatus('Signature flow restored. Please continue collecting signatures.');
                } else {
                    logger.warn('⚠️ No signature flow data found to recover');
                    setExecutionStatus('No signature flow data found to recover.');
                }
            } catch (error) {
                logger.error('❌ Error during recovery:', error);
                setExecutionStatus('Error recovering signature flow state.');
            }
        };

        // Listen for recovery events
        window.addEventListener('forceStateRecovery', handleRecovery);

        // Cleanup
        return () => {
            window.removeEventListener('forceStateRecovery', handleRecovery);
        };
    }, [contracts]); // Depend on contracts so we can restore contract selection

    // Local state for non-persistent data
    const [functions, setFunctions] = useState<any[]>([]);
    const [encryptedFunctionData, setEncryptedFunctionData] = useState<string>('');

    // Workflow integration state
    const [showWorkflowShortcuts, setShowWorkflowShortcuts] = useState(false);

    // Workflow filtering state - capture and persist workflow data IMMEDIATELY
    const [workflowFilterTarget, setWorkflowFilterTarget] = useState<string | null>(() => {
        // Capture workflow data immediately on component initialization
        try {
            const autoSelection = sessionStorage.getItem('workflowAutoSelection');
            if (autoSelection) {
                const data = JSON.parse(autoSelection);
                return data.functionName || null;
            }
        } catch (error) {
            logger.error('Error capturing workflow data on init:', error);
        }
        return null;
    });

    // Additional state to track if we're in workflow mode
    const [isWorkflowMode, setIsWorkflowMode] = useState<boolean>(() => {
        try {
            const autoSelection = sessionStorage.getItem('workflowAutoSelection');
            if (autoSelection) {
                const data = JSON.parse(autoSelection);
                return data.workflowContext === true;
            }
        } catch (error) {
            logger.error('Error checking workflow mode:', error);
        }
        return false;
    });

    // Add effect to clear error and success messages when entering workflow mode
    useEffect(() => {
        if (isWorkflowMode && workflowFilterTarget) {
            logger.log('🎯 Entering workflow mode - clearing any stale messages');

            // Clear any existing error or success messages when starting a new workflow
            if (tabState.executionStatus) {
                const hasStaleMessage =
                    tabState.executionStatus.toLowerCase().includes('error') ||
                    tabState.executionStatus.toLowerCase().includes('success') ||
                    tabState.executionStatus.includes('Transaction executed:') ||
                    tabState.executionStatus.includes('✅ Transaction executed successfully');

                if (hasStaleMessage) {
                    logger.log('🧹 Clearing stale messages for new workflow execution');
                    setTabState({ executionStatus: '' });
                }
            }

            // Clear any stale results from previous executions
            if (tabState.results && Object.keys(tabState.results).length > 0) {
                const hasStaleResults = Object.values(tabState.results).some(result =>
                    result.includes('Transaction executed:') ||
                    result.includes('0x')
                );

                if (hasStaleResults) {
                    logger.log('🧹 Clearing stale results for new workflow execution');
                    setTabState({ results: {} });
                }
            }
        }
    }, [isWorkflowMode, workflowFilterTarget]); // Run when workflow mode changes

    // Extract persistent state
    const selectedContract = tabState.selectedContract;
    const selectedFunction = tabState.selectedFunction;
    const functionInputs = tabState.functionInputs;
    const results = tabState.results;
    const executionStatus = tabState.executionStatus;
    const showSignatureFlow = tabState.showSignatureFlow;
    const owners = tabState.owners;

    // Helper functions to update persistent state
    const setSelectedContract = (contract: any) => {
        setTabState({ selectedContract: contract });
    };

    const setSelectedFunction = (functionName: string | null) => {
        setTabState({ selectedFunction: functionName });
    };

    const setFunctionInputs = (inputs: { [key: string]: any[] }) => {
        setTabState({ functionInputs: inputs });
    };

    const setResults = (results: { [key: string]: string }) => {
        setTabState({ results });
    };

    const setExecutionStatus = (status: string) => {
        setTabState({ executionStatus: status });
    };

    const setShowSignatureFlow = (show: boolean) => {
        setTabState({ showSignatureFlow: show });
    };

    const setOwners = (owners: string[]) => {
        setTabState({ owners });
    };
    const { addSignature, clearSignatures } = useSignatureContext();

    // Floating converter for hex values
    const { openConverter } = useFloatingConverter();

    // Auto-selection from workflow context - SINGLE consolidated workflow processing
    useEffect(() => {
        if (contracts.length === 0) return; // Wait for contracts to load

        try {
            const autoSelection = sessionStorage.getItem('workflowAutoSelection');
            if (autoSelection) {
                const data = JSON.parse(autoSelection);
                logger.log('Processing workflow auto-selection in FunctionExplorer:', data);

                // Find and select the contract
                const contract = contracts.find(c => c.address === data.contractAddress);
                if (contract) {
                    logger.log('Auto-selecting contract:', contract.name);

                    // Set contract if not already set
                    if (!selectedContract || selectedContract.address !== contract.address) {
                        setSelectedContract(contract);
                    }

                    // Set function if not already set
                    if (!selectedFunction || selectedFunction !== data.functionName) {
                        setSelectedFunction(data.functionName);
                    }

                    // CRITICAL: Set workflow filter target BEFORE clearing sessionStorage
                    if (data.functionName && data.workflowContext && !workflowFilterTarget) {
                        setWorkflowFilterTarget(data.functionName);
                    }

                    // Auto-populate function inputs for MultiSig functions
                    if (data.functionName === 'execTransaction' || data.functionName === 'executeTransactionWithChecks') {
                        logger.log('🔐 Auto-populating MultiSig function:', data.functionName);

                        if (data.prefilledInputs) {
                            logger.log('✅ Using pre-filled inputs from workflow:', data.prefilledInputs);

                            // Map the pre-filled inputs to the MultiSig function parameters
                            const autoInputs = [
                                data.prefilledInputs.to || '',                    // to (address)
                                data.prefilledInputs.value || '0',               // value (uint256)
                                data.prefilledInputs.data || '0x',               // data (bytes)
                                data.prefilledInputs.operation || '0',           // operation (uint8)
                                data.prefilledInputs.safeTxGas || '0',           // safeTxGas (uint256)
                                data.prefilledInputs.baseGas || '0',             // baseGas (uint256)
                                data.prefilledInputs.gasPrice || '0',            // gasPrice (uint256)
                                data.prefilledInputs.gasToken || '0x0000000000000000000000000000000000000000', // gasToken (address)
                                data.prefilledInputs.refundReceiver || '0x0000000000000000000000000000000000000000', // refundReceiver (address)
                                data.prefilledInputs.signatures || '0x'          // signatures (bytes)
                            ];

                            setFunctionInputs({
                                ...functionInputs,
                                [data.functionName]: autoInputs
                            });

                            logger.log('🎯 Auto-populated MultiSig inputs:', {
                                to: autoInputs[0],
                                value: autoInputs[1],
                                data: autoInputs[2].startsWith('{{') ? autoInputs[2] : autoInputs[2].substring(0, 20) + '...',
                                operation: autoInputs[3],
                                safeTxGas: autoInputs[4],
                                baseGas: autoInputs[5],
                                gasPrice: autoInputs[6],
                                gasToken: autoInputs[7].substring(0, 10) + '...',
                                refundReceiver: autoInputs[8].substring(0, 10) + '...',
                                signatures: autoInputs[9]
                            });
                        } else {
                            logger.warn('⚠️ No pre-filled inputs found for MultiSig function');
                        }
                    }
                    // Auto-populate function inputs for Timelock execute function
                    else if (data.functionName === 'execute' && data.prefilledInputs) {
                        logger.log('⚡ Auto-populating timelock execute function:', data.functionName);
                        logger.log('✅ Using pre-filled inputs from selected schedule:', data.prefilledInputs);

                        // CRITICAL FIX: Handle empty salt by attempting extraction from schedule data
                        let saltValue = data.prefilledInputs.salt || '';
                        let saltDisplayMessage = '';

                        if (!saltValue || saltValue === '0' || saltValue === '') {
                            logger.warn('⚠️ Salt is missing from prefilledInputs, attempting to extract from schedule data...');

                            // Try to get salt from the schedule data that was passed to the workflow
                            try {
                                const scheduleData = data.prefilledInputs.scheduleData || data.scheduleData;
                                if (scheduleData && scheduleData.salt && scheduleData.salt !== '0') {
                                    saltValue = scheduleData.salt;
                                    logger.log('✅ Extracted salt from schedule data:', saltValue);
                                } else {
                                    // Try to get from sessionStorage as fallback
                                    const storedScheduleData = sessionStorage.getItem('step_step0_schedule_selection_outputs');
                                    if (storedScheduleData) {
                                        const parsedScheduleData = JSON.parse(storedScheduleData);
                                        if (parsedScheduleData.salt && parsedScheduleData.salt !== '0') {
                                            saltValue = parsedScheduleData.salt;
                                            logger.log('✅ Extracted salt from sessionStorage schedule data:', saltValue);
                                        }
                                    }
                                }
                            } catch (error) {
                                logger.error('❌ Error extracting salt from schedule data:', error);
                            }

                            if (!saltValue || saltValue === '0' || saltValue === '') {
                                saltDisplayMessage = 'MISSING - Salt was not captured from original schedule';
                                logger.error('❌ CRITICAL ISSUE: Salt is missing from schedule data!', {
                                    originalSalt: data.prefilledInputs.salt,
                                    allPrefilledInputs: data.prefilledInputs
                                });
                            } else {
                                saltDisplayMessage = saltValue.substring(0, 20) + '...';
                            }
                        } else {
                            saltDisplayMessage = saltValue.substring(0, 20) + '...';
                        }

                        // Map the pre-filled inputs to the timelock execute function parameters
                        // Parameters: address target, uint256 value, bytes data, bytes32 predecessor, bytes32 salt
                        const autoInputs = [
                            data.prefilledInputs.target || '',           // target (address)
                            data.prefilledInputs.value || '0',           // value (uint256) 
                            data.prefilledInputs.data || '0x',           // data (bytes) - KV reference from schedule
                            data.prefilledInputs.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000', // predecessor (bytes32)
                            saltValue                                     // salt (bytes32) - CRITICAL: Use actual salt value
                        ];

                        setFunctionInputs({
                            ...functionInputs,
                            [data.functionName]: autoInputs
                        });

                        logger.log('🎯 Auto-populated timelock execute inputs:', {
                            target: autoInputs[0],
                            value: autoInputs[1],
                            data: autoInputs[2].startsWith('{{') ? autoInputs[2] : autoInputs[2].substring(0, 20) + '...',
                            predecessor: autoInputs[3].substring(0, 20) + '...',
                            salt: saltDisplayMessage,
                            saltIssue: !saltValue || saltValue === '0' ? 'SALT IS MISSING OR ZERO' : 'Salt OK'
                        });

                        // CRITICAL: Show error alert if salt is missing
                        if (!saltValue || saltValue === '0' || saltValue === '') {
                            logger.error('🚨 WORKFLOW BLOCKER: Cannot proceed without salt from original schedule');
                        }
                    }

                    // Clear the auto-selection data after use
                    sessionStorage.removeItem('workflowAutoSelection');
                    logger.log('🧹 Cleared workflow auto-selection data');
                } else {
                    logger.warn('⚠️ Contract not found for auto-selection:', data.contractAddress);
                }
            }
        } catch (error) {
            logger.error('❌ Error processing workflow auto-selection:', error);
        }
    }, [contracts, selectedContract, selectedFunction, workflowFilterTarget]); // Depend on contracts being loaded and current state

    // Fetch owners from the contract when selectedContract changes
    useEffect(() => {
        const fetchOwners = async () => {
            if (!selectedContract || !signer) return;

            try {
                // Check if the contract has a getOwners function
                const hasGetOwners = selectedContract.abi.some(
                    (item: any) => item.type === 'function' && item.name === 'getOwners'
                );

                if (hasGetOwners) {
                    const contract = new ethers.Contract(
                        selectedContract.address,
                        selectedContract.abi,
                        signer
                    );

                    const fetchedOwners = await contract.getOwners();
                    setOwners(fetchedOwners);
                    logger.log("Fetched owners:", fetchedOwners);
                }
            } catch (error) {
                logger.error("Error fetching owners:", error);
            }
        };

        fetchOwners();
    }, [selectedContract, signer]);

    // Update available functions when contract changes
    useEffect(() => {
        if (selectedContract && selectedContract.abi) {
            let contractFunctions = selectedContract.abi.filter(
                (item: any) => item.type === 'function'
            );

            logger.log(`Contract: ${selectedContract.name}, ABI items: ${selectedContract.abi.length}, Functions: ${contractFunctions.length}`);

            // Apply filtering if we have a workflow filter target AND we're in workflow mode
            if (workflowFilterTarget && isWorkflowMode) {
                const originalCount = contractFunctions.length;

                // More robust filtering - check both name and potential variations
                const filteredFunctions = contractFunctions.filter((func: any) => {
                    return func.name === workflowFilterTarget;
                });

                if (filteredFunctions.length > 0) {
                    contractFunctions = filteredFunctions;
                    logger.log(`Workflow mode: Filtered from ${originalCount} to ${contractFunctions.length} function(s) - showing only: ${workflowFilterTarget}`);
                } else {
                    logger.warn(`Function ${workflowFilterTarget} not found in contract ABI - showing all functions`);
                    // Don't filter if target function not found - show all functions as fallback
                }
            } else if (workflowFilterTarget && !isWorkflowMode) {
                logger.log(`Have filter target but not in workflow mode - showing all functions`);
            } else {
                logger.log(`No workflow filter target - showing all functions`);
            }

            // Log specific functions we're looking for
            const targetFunctions = ['execTransaction', 'executeTransactionWithChecks', 'getOwnedTokenIds', 'getTotalLPTokens'];
            targetFunctions.forEach(funcName => {
                const found = contractFunctions.find((f: any) => f.name === funcName);
                logger.log(`[FunctionExplorer] ${funcName}: ${found ? 'FOUND' : 'MISSING'}`);
            });

            // Log all function names for debugging
            const functionNames = contractFunctions.map((f: any) => f.name).sort();
            logger.log(`[FunctionExplorer] Final functions to display: ${functionNames.join(', ')}`);

            setFunctions(contractFunctions);
        } else {
            logger.log(`[FunctionExplorer] No contract selected or missing ABI`);
            setFunctions([]);
        }
    }, [selectedContract, workflowFilterTarget, isWorkflowMode]);

    // Handle contract selection
    const handleContractSelect = (contract: any) => {
        setSelectedContract(contract);
        setSelectedFunction(null);
        setFunctionInputs({});
        setResults({});
    };

    // Handle function selection
    const handleFunctionSelect = (functionName: string) => {
        setSelectedFunction(functionName);
        // Reset inputs for the new function
        const updatedInputs = {
            ...functionInputs,
            [functionName]: functionInputs[functionName] || []
        };
        setFunctionInputs(updatedInputs);
    };

    // Handle input change with secure data retrieval
    const handleInputChange = async (functionName: string, value: string, index: number) => {
        let processedValue = value;

        // Check if the value is a secure workflow reference - but don't decrypt it here
        if (value && backendDataHandler.isSecureReference(value)) {
            // Keep the reference as-is for UI display, don't decrypt
            processedValue = value;
        }

        const updatedValues = functionInputs[functionName] ? [...functionInputs[functionName]] : [];
        updatedValues[index] = processedValue;
        const updatedInputs = { ...functionInputs, [functionName]: updatedValues };
        setFunctionInputs(updatedInputs);
    };

    // Store parsed results with structured data
    const [parsedResults, setParsedResults] = useState<{ [key: string]: any }>({});

    // Execute read function
    const executeReadFunction = async (functionName: string) => {
        if (!provider || !selectedContract) return;

        try {
            setExecutionStatus(`Executing ${functionName}...`);

            // TIMESTAMP-BASED CACHE BUSTING: Use timestamp instead of block number for forked chains
            let freshProvider = provider;
            if (window.ethereum) {
                // Create a completely new provider instance with unique timestamp-based name
                const timestamp = Date.now();
                freshProvider = new ethers.providers.Web3Provider(
                    window.ethereum as any,
                    {
                        name: `fresh-call-${timestamp}`, // Unique timestamp prevents caching
                        chainId: provider.network?.chainId || 31337,
                        ensAddress: undefined // Disable ENS resolution
                    }
                );

                // CRITICAL: Force fresh call to ensure provider is initialized
                try {
                    await freshProvider.getNetwork();
                    logger.log(`🔄 TIMESTAMP cache bust: Fresh provider created at ${timestamp}`);
                } catch (error) {
                    logger.warn('Failed to create fresh provider, using existing one:', error);
                    freshProvider = provider;
                }
            }

            const contract = new ethers.Contract(
                selectedContract.address,
                selectedContract.abi,
                freshProvider
            );

            // Get the inputs for this function
            const inputs = functionInputs[functionName] || [];

            // Get the function ABI to identify address parameters
            const functionAbi = selectedContract.abi.find(
                (item: any) => item.type === 'function' && item.name === functionName
            );

            // Process inputs to handle address parameters correctly
            const processedInputs = inputs.map((input: any, index: number) => {
                // Check if this parameter is an address type
                const isAddressParam = functionAbi?.inputs?.[index]?.type === 'address';

                if (isAddressParam && typeof input === 'string') {
                    // For address parameters, ensure they're treated as addresses not ENS names
                    // by converting to checksummed addresses
                    try {
                        return ethers.utils.getAddress(input);
                    } catch (e) {
                        // If not a valid address, return as is
                        return input;
                    }
                }
                return input;
            });

            // TIMESTAMP-BASED CACHE BUSTING: Use 'latest' with fresh provider
            // On forked chains, block number doesn't change, so we rely on fresh provider
            const callOverrides = {
                blockTag: 'latest' as const
            };

            logger.log(`🔍 TIMESTAMP cache bust: Calling ${functionName} with fresh provider:`, {
                contractAddress: selectedContract.address,
                inputs: processedInputs,
                timestamp: new Date().toISOString()
            });

            // Call the function with processed inputs and timestamp-based cache-busting
            const result = await contract[functionName](...processedInputs, callOverrides);

            // 🎯 PHASE 1 FIX: Use new complex result parser
            const parsedResult = parseComplexResult(result, functionName, selectedContract.abi);

            // Update results - store formatted text
            const updatedResults = {
                ...results,
                [functionName]: parsedResult.formatted
            };
            setResults(updatedResults);

            // Store structured data for special display types
            if (parsedResult.structured && parsedResult.displayType === 'burst') {
                setParsedResults({
                    ...parsedResults,
                    [functionName]: parsedResult
                });
            }

            // Log the result for debugging
            logger.log(`✅ ${functionName} result (cache-busted):`, {
                raw: result,
                formatted: parsedResult.formatted,
                displayType: parsedResult.displayType,
                hasStructuredData: !!parsedResult.structured,
                timestamp: new Date().toISOString()
            });

            setExecutionStatus('');
        } catch (error) {
            logger.error("Error executing function:", error);
            setExecutionStatus(`Error: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    // Execute write function
    const executeWriteFunction = async (functionName: string) => {
        if (!signer || !selectedContract) return;

        try {
            setExecutionStatus(`Executing ${functionName}...`);

            const contract = new ethers.Contract(
                selectedContract.address,
                selectedContract.abi,
                signer
            );

            // Get the inputs for this function
            const inputs = functionInputs[functionName] || [];

            // Special handling for timelockController.schedule function
            if (functionName === 'schedule') {
                // PHASE 1 DEBUG: Log all inputs before processing
                logger.log('🔍 PHASE 1 DEBUG - Schedule function inputs before processing:', {
                    functionName,
                    inputsLength: inputs.length,
                    input0_target: inputs[0],
                    input1_value: inputs[1],
                    input2_data: inputs[2]?.substring(0, 50) + '...',
                    input3_predecessor: inputs[3],
                    input4_salt: inputs[4],
                    input5_delay: inputs[5],
                    allInputs: inputs
                });

                // Validate and format inputs for schedule
                // Typical parameters: address target, uint256 value, bytes data, bytes32 predecessor, bytes32 salt, uint256 delay

                // Enhanced validation and normalization for 'target' address
                try {
                    if (!inputs[0]) {
                        throw new Error("Missing 'target' address");
                    }

                    // Normalize the address to checksum format
                    inputs[0] = ethers.utils.getAddress(inputs[0]);
                    logger.log("'target' address normalized:", inputs[0]);
                } catch (error) {
                    logger.error("Invalid 'target' address:", inputs[0], error);
                    throw new Error(`Invalid 'target' address: ${inputs[0]}`);
                }

                // Format value as BigNumber (usually in wei)
                if (inputs[1] === undefined || inputs[1] === '') {
                    inputs[1] = ethers.utils.parseEther('0');
                } else {
                    try {
                        inputs[1] = ethers.utils.parseEther(inputs[1].toString());
                    } catch (e) {
                        logger.warn("Could not parse value as ether, using as is:", inputs[1]);
                    }
                }

                // CRITICAL FIX: Handle KV reference decryption for data parameter and capture decrypted hex
                let decryptedMemerHex = '';
                if (inputs[2] !== undefined && typeof inputs[2] === 'string') {
                    let val = inputs[2];

                    // Check if val is a JSON object string and extract functionData
                    if (val && typeof val === 'string' && val.startsWith('{') && val.includes('functionData')) {
                        try {
                            const jsonObj = JSON.parse(val);
                            if (jsonObj.functionData) {
                                val = jsonObj.functionData;
                            }
                        } catch (error) {
                            logger.warn(`Failed to parse JSON object for data parameter:`, error);
                        }
                    }

                    // Handle cases where the reference might have been prefixed with 0x
                    let cleanVal = val;
                    if (val && typeof val === 'string' && val.startsWith('0x{{') && val.endsWith('}}')) {
                        cleanVal = val.substring(2); // Remove 0x prefix
                    }

                    // Check for secure reference and decrypt if needed
                    const isSecureRef = backendDataHandler.isSecureReference(cleanVal);
                    if (cleanVal && typeof cleanVal === 'string' && isSecureRef) {
                        try {
                            logger.log('🔍 OPERATIONID FIX: Decrypting KV reference for data parameter:', cleanVal);
                            const result = await backendDataHandler.autoDecryptIfNeeded(cleanVal);
                            if (result.success && result.data) {
                                // Check if retrieved data is a function metadata object
                                if (typeof result.data === 'object' && result.data !== null && 'functionData' in result.data) {
                                    val = (result.data as any).functionData;
                                } else if (typeof result.data === 'string') {
                                    // Check if the string data is actually a JSON object
                                    if (result.data.startsWith('{') && result.data.includes('functionData')) {
                                        try {
                                            const jsonObj = JSON.parse(result.data);
                                            if (jsonObj.functionData) {
                                                val = jsonObj.functionData;
                                            } else {
                                                val = result.data;
                                            }
                                        } catch (error) {
                                            val = result.data;
                                        }
                                    } else {
                                        val = result.data;
                                    }
                                } else {
                                    val = String(result.data);
                                }

                                // CRITICAL: Capture the decrypted hex data for operationId computation
                                decryptedMemerHex = val;
                                logger.log('✅ OPERATIONID FIX: Captured decrypted Memer hex data:', decryptedMemerHex.substring(0, 50) + '...');
                            } else {
                                throw new Error(`Failed to retrieve workflow data: ${result.error}`);
                            }
                        } catch (error) {
                            throw new Error(`Backend retrieval failed: ${error instanceof Error ? error.message : String(error)}`);
                        }
                    } else {
                        // Not a KV reference, use as-is
                        decryptedMemerHex = val;
                    }

                    // Format data as bytes if it's a string
                    if (!val.startsWith('0x')) {
                        val = '0x' + val;
                    }
                    inputs[2] = val;
                } else if (inputs[2] === undefined || inputs[2] === '') {
                    throw new Error("Missing 'data' parameter");
                }

                // predecessor (bytes32)
                if (inputs[3] === undefined || inputs[3] === '') {
                    inputs[3] = ethers.constants.HashZero; // Use zero hash as default
                }

                // PHASE 1 DEBUG: Enhanced salt validation with detailed logging
                logger.log('🧂 PHASE 1 DEBUG - Salt validation:', {
                    saltValue: inputs[4],
                    saltType: typeof inputs[4],
                    saltLength: inputs[4]?.length,
                    isUndefined: inputs[4] === undefined,
                    isEmpty: inputs[4] === '',
                    isZero: inputs[4] === '0'
                });

                // salt (bytes32) - should be provided by the user or generated
                if (inputs[4] === undefined || inputs[4] === '' || inputs[4] === '0') {
                    logger.error('🚨 PHASE 1 CRITICAL: Salt is missing or zero!', {
                        saltValue: inputs[4],
                        functionInputsForSchedule: functionInputs['schedule'],
                        allFunctionInputs: functionInputs
                    });
                    throw new Error(`Missing or invalid 'salt' parameter: "${inputs[4]}". Please use the Generate Salt button.`);
                }

                // PHASE 1 DEBUG: Enhanced delay validation with detailed logging  
                logger.log('⏰ PHASE 1 DEBUG - Delay validation:', {
                    delayValue: inputs[5],
                    delayType: typeof inputs[5],
                    isUndefined: inputs[5] === undefined,
                    isEmpty: inputs[5] === '',
                    isZero: inputs[5] === '0'
                });

                // delay (uint256)
                if (inputs[5] === undefined || inputs[5] === '') {
                    logger.error('🚨 PHASE 1 CRITICAL: Delay is missing!', {
                        delayValue: inputs[5],
                        functionInputsForSchedule: functionInputs['schedule'],
                        allFunctionInputs: functionInputs
                    });
                    throw new Error(`Missing 'delay' parameter: "${inputs[5]}". Please use the Get Min Delay button.`);
                } else {
                    // Convert to BigNumber
                    try {
                        inputs[5] = ethers.BigNumber.from(inputs[5]);
                        logger.log('✅ PHASE 1 DEBUG: Delay converted to BigNumber:', inputs[5].toString());
                    } catch (e) {
                        logger.warn("Could not parse delay as BigNumber, using as is:", inputs[5]);
                    }
                }

                logger.log("🎯 PHASE 1 DEBUG: Executing schedule with formatted inputs:", {
                    target: inputs[0],
                    value: inputs[1]?.toString(),
                    dataLength: inputs[2]?.length,
                    predecessor: inputs[3],
                    salt: inputs[4],
                    delay: inputs[5]?.toString(),
                    allInputs: inputs
                });
            }
            // Special handling for timelockController.execute function
            else if (functionName === 'execute') {
                // CRITICAL FIX: Add nested KV reference resolution (same pattern as FunctionBuilder.tsx)
                logger.log('🔍 NESTED DECRYPTION: Starting parameter resolution for timelock.execute');

                // Helper function to resolve parameters that might contain KV references
                const resolveParameter = async (paramIndex: number, paramName: string, defaultValue: string) => {
                    const rawValue = inputs[paramIndex] || defaultValue;

                    logger.log(`🔍 NESTED DECRYPTION: Checking parameter ${paramIndex} (${paramName}):`, {
                        rawValue: typeof rawValue === 'string' ? rawValue.substring(0, 50) + '...' : rawValue,
                        isKVReference: rawValue && typeof rawValue === 'string' && backendDataHandler.isSecureReference(rawValue)
                    });

                    // Check if this parameter is a KV reference
                    if (rawValue && typeof rawValue === 'string' && backendDataHandler.isSecureReference(rawValue)) {
                        try {
                            logger.log(`🔐 NESTED DECRYPTION: Decrypting KV reference for ${paramName}:`, rawValue);
                            const result = await backendDataHandler.autoDecryptIfNeeded(rawValue);
                            if (result.success && result.data) {
                                // Extract the actual value
                                let resolvedValue;
                                if (typeof result.data === 'object' && result.data !== null && 'functionData' in result.data) {
                                    resolvedValue = (result.data as any).functionData;
                                    logger.log(`✅ NESTED DECRYPTION: Extracted functionData for ${paramName}`);
                                } else if (typeof result.data === 'string') {
                                    resolvedValue = result.data;
                                    logger.log(`✅ NESTED DECRYPTION: Using string data for ${paramName}`);
                                } else {
                                    resolvedValue = String(result.data);
                                    logger.log(`✅ NESTED DECRYPTION: Converted to string for ${paramName}`);
                                }

                                logger.log(`✅ NESTED DECRYPTION: Successfully resolved ${paramName}:`, resolvedValue.substring(0, 50) + '...');
                                return resolvedValue;
                            }
                            logger.warn(`⚠️ NESTED DECRYPTION: Failed to resolve KV reference for ${paramName}`);
                            return rawValue; // Fallback
                        } catch (error) {
                            logger.error(`❌ NESTED DECRYPTION: Error resolving KV reference for ${paramName}:`, error);
                            return rawValue; // Fallback
                        }
                    }

                    logger.log(`✅ NESTED DECRYPTION: Parameter ${paramName} is not a KV reference, using as-is`);
                    return rawValue;
                };

                // CRITICAL: Resolve ALL parameters (not just data) - this catches nested KV references
                logger.log('🔍 NESTED DECRYPTION: Resolving all timelock.execute parameters...');
                inputs[0] = await resolveParameter(0, 'target', ''); // target address
                inputs[1] = await resolveParameter(1, 'value', '0'); // value
                inputs[2] = await resolveParameter(2, 'payload/data', '0x'); // payload/data - THIS IS THE CRITICAL ONE
                inputs[3] = await resolveParameter(3, 'predecessor', ethers.constants.HashZero); // predecessor
                inputs[4] = await resolveParameter(4, 'salt', ''); // salt

                logger.log('✅ NESTED DECRYPTION: All parameters resolved, now validating and formatting...');

                // Now validate and format the resolved values
                // Enhanced validation and normalization for 'target' address
                try {
                    if (!inputs[0]) {
                        throw new Error("Missing 'target' address");
                    }

                    // Normalize the address to checksum format
                    inputs[0] = ethers.utils.getAddress(inputs[0]);
                    logger.log("'target' address normalized:", inputs[0]);
                } catch (error) {
                    logger.error("Invalid 'target' address:", inputs[0], error);
                    throw new Error(`Invalid 'target' address: ${inputs[0]}`);
                }

                // Format value as BigNumber (usually in wei)
                if (inputs[1] === undefined || inputs[1] === '') {
                    inputs[1] = ethers.utils.parseEther('0');
                } else {
                    try {
                        inputs[1] = ethers.utils.parseEther(inputs[1].toString());
                    } catch (e) {
                        logger.warn("Could not parse value as ether, using as is:", inputs[1]);
                    }
                }

                // Format data as bytes if it's a string (after resolution)
                if (inputs[2] !== undefined && typeof inputs[2] === 'string') {
                    // Prevent double 0x prefix
                    if (inputs[2].startsWith('0x0x')) {
                        inputs[2] = inputs[2].substring(2);
                    }
                    // Ensure hex data is properly formatted
                    if (!inputs[2].startsWith('0x')) {
                        inputs[2] = '0x' + inputs[2];
                    }
                    logger.log("✅ NESTED DECRYPTION: Payload/data formatted:", inputs[2].substring(0, 50) + '...');
                } else if (inputs[2] === undefined || inputs[2] === '') {
                    throw new Error("Missing 'data' parameter");
                }

                // predecessor (bytes32)
                if (inputs[3] === undefined || inputs[3] === '') {
                    inputs[3] = ethers.constants.HashZero; // Use zero hash as default
                }

                // salt (bytes32) - should be provided by the user or generated
                if (inputs[4] === undefined || inputs[4] === '') {
                    throw new Error("Missing 'salt' parameter. Please use the Generate Salt button.");
                }

                logger.log("✅ NESTED DECRYPTION: Executing timelock.execute with fully resolved and formatted inputs:", {
                    target: inputs[0],
                    value: inputs[1]?.toString(),
                    dataLength: inputs[2]?.length,
                    dataPreview: inputs[2]?.substring(0, 50) + '...',
                    predecessor: inputs[3],
                    salt: inputs[4]?.substring(0, 20) + '...'
                });
            }
            // Special handling for execTransaction and executeTransactionWithChecks functions in MultiSig contracts
            else if (functionName === 'execTransaction' || functionName === 'executeTransactionWithChecks') {
                logger.log('🎯 DIAGNOSTIC: Entered MultiSig execution block', {
                    functionName,
                    inputsLength: inputs.length,
                    inputsPreview: inputs.map((inp, idx) => ({
                        index: idx,
                        type: typeof inp,
                        value: typeof inp === 'string' ? inp.substring(0, 30) + '...' : String(inp)
                    }))
                });

                // 🔒 CRITICAL SECURITY FIX: Create execution copy to prevent UI exposure
                // We decrypt into this copy for blockchain execution, but NEVER update React state
                logger.log('🎯 DIAGNOSTIC: About to create execution copy...');
                const executionInputs = [...inputs];
                logger.log('✅ DIAGNOSTIC: Execution copy created', {
                    executionInputsLength: executionInputs.length,
                    originalInputsLength: inputs.length
                });

                // Validate and format inputs for execTransaction
                // Typical parameters: address to, uint256 value, bytes data, uint8 operation, ...

                // Enhanced validation and normalization for 'to' address - USE EXECUTION COPY
                logger.log('🎯 DIAGNOSTIC: About to validate "to" address...');
                try {
                    if (!executionInputs[0]) {
                        throw new Error("Missing 'to' address");
                    }

                    // Normalize the address to checksum format
                    executionInputs[0] = ethers.utils.getAddress(executionInputs[0]);
                    logger.log("✅ DIAGNOSTIC: 'to' address normalized:", executionInputs[0]);
                } catch (error) {
                    logger.error("❌ DIAGNOSTIC: Invalid 'to' address:", executionInputs[0], error);
                    throw new Error(`Invalid 'to' address: ${executionInputs[0]}`);
                }

                // Format value as BigNumber (usually in wei) - USE EXECUTION COPY
                logger.log('🎯 DIAGNOSTIC: About to format value...');
                if (executionInputs[1] !== undefined && executionInputs[1] !== '') {
                    try {
                        const valueStr = String(executionInputs[1]);

                        // CRITICAL FIX: Check if value is already in wei (no decimal point, large number)
                        // or if it's in ETH (has decimal point or small number)
                        if (valueStr.includes('.') || parseFloat(valueStr) < 1000) {
                            // Value looks like ETH (has decimal or is small number like 0.001)
                            executionInputs[1] = ethers.utils.parseEther(valueStr);
                            logger.log("✅ DIAGNOSTIC: Value formatted from ETH to wei:", executionInputs[1].toString());
                        } else {
                            // Value looks like wei (large number, no decimal)
                            // Use BigNumber.from to avoid scientific notation issues
                            executionInputs[1] = ethers.BigNumber.from(valueStr);
                            logger.log("✅ DIAGNOSTIC: Value already in wei, converted to BigNumber:", executionInputs[1].toString());
                        }
                    } catch (e) {
                        logger.warn("⚠️ DIAGNOSTIC: Could not parse value, using as is:", executionInputs[1], e);
                        // Fallback: try BigNumber.from for wei values
                        try {
                            executionInputs[1] = ethers.BigNumber.from(String(executionInputs[1]));
                        } catch (fallbackError) {
                            logger.error("❌ DIAGNOSTIC: Failed to parse value even with fallback:", fallbackError);
                        }
                    }
                }

                // 🔒 Handle KV reference decryption for data parameter - DECRYPT INTO EXECUTION COPY ONLY
                if (executionInputs[2] !== undefined && typeof executionInputs[2] === 'string') {
                    let val = executionInputs[2];
                    logger.log('🔒 Processing data field - original value:', val.startsWith('{{') ? val : val.substring(0, 50) + '...');

                    // Check if val is a JSON object string and extract functionData
                    if (val && typeof val === 'string' && val.startsWith('{') && val.includes('functionData')) {
                        try {
                            const jsonObj = JSON.parse(val);
                            if (jsonObj.functionData) {
                                val = jsonObj.functionData;
                            }
                        } catch (error) {
                            logger.warn(`Failed to parse JSON object for data parameter:`, error);
                        }
                    }

                    // Handle cases where the reference might have been prefixed with 0x
                    let cleanVal = val;
                    if (val && typeof val === 'string' && val.startsWith('0x{{') && val.endsWith('}}')) {
                        cleanVal = val.substring(2); // Remove 0x prefix
                    }

                    // Check for secure reference and decrypt if needed - DECRYPT INTO EXECUTION COPY
                    const isSecureRef = backendDataHandler.isSecureReference(cleanVal);
                    if (cleanVal && typeof cleanVal === 'string' && isSecureRef) {
                        try {
                            logger.log('🔐 Decrypting KV reference for execution...');
                            const result = await backendDataHandler.autoDecryptIfNeeded(cleanVal);
                            if (result.success && result.data) {
                                // Check if retrieved data is a function metadata object
                                if (typeof result.data === 'object' && result.data !== null && 'functionData' in result.data) {
                                    val = (result.data as any).functionData;
                                } else if (typeof result.data === 'string') {
                                    // Check if the string data is actually a JSON object
                                    if (result.data.startsWith('{') && result.data.includes('functionData')) {
                                        try {
                                            const jsonObj = JSON.parse(result.data);
                                            if (jsonObj.functionData) {
                                                val = jsonObj.functionData;
                                            } else {
                                                val = result.data;
                                            }
                                        } catch (error) {
                                            val = result.data;
                                        }
                                    } else {
                                        val = result.data;
                                    }
                                } else {
                                    val = String(result.data);
                                }
                                logger.log('✅ Decrypted data for execution:', val.substring(0, 50) + '...');
                            } else {
                                throw new Error(`Failed to retrieve workflow data: ${result.error}`);
                            }
                        } catch (error) {
                            throw new Error(`Backend retrieval failed: ${error instanceof Error ? error.message : String(error)}`);
                        }
                    }

                    // Prevent double 0x prefix
                    if (val && typeof val === 'string' && val.startsWith('0x0x')) {
                        val = val.substring(2);
                    }

                    // 🔒 CRITICAL: Update EXECUTION COPY only, NOT original inputs
                    if (val && typeof val === 'string') {
                        if (val.startsWith('0x') && val.length > 2) {
                            executionInputs[2] = val;
                        } else if (!val.startsWith('0x')) {
                            executionInputs[2] = '0x' + val;
                        } else {
                            executionInputs[2] = val;
                        }
                    } else {
                        executionInputs[2] = val;
                    }
                    logger.log('🔒 Updated execution copy - original inputs unchanged');
                }

                // Set default values for other parameters - USE EXECUTION COPY
                // operation (uint8)
                if (executionInputs[3] === undefined || executionInputs[3] === '') {
                    executionInputs[3] = '0';
                }

                // safeTxGas (uint256)
                if (executionInputs[4] === undefined || executionInputs[4] === '') {
                    executionInputs[4] = '0';
                }

                // baseGas (uint256)
                if (executionInputs[5] === undefined || executionInputs[5] === '') {
                    executionInputs[5] = '0';
                }

                // gasPrice (uint256)
                if (executionInputs[6] === undefined || executionInputs[6] === '') {
                    executionInputs[6] = '0';
                }

                // gasToken (address) - Enhanced validation - USE EXECUTION COPY
                try {
                    if (executionInputs[7] === undefined || executionInputs[7] === '') {
                        executionInputs[7] = ethers.constants.AddressZero;
                    } else {
                        // Normalize the address to checksum format
                        executionInputs[7] = ethers.utils.getAddress(executionInputs[7]);
                    }
                    logger.log("gasToken address normalized:", executionInputs[7]);
                } catch (error) {
                    logger.error("Invalid gasToken address:", executionInputs[7], error);
                    throw new Error(`Invalid gasToken address: ${executionInputs[7]}`);
                }

                // refundReceiver (address) - Enhanced validation - USE EXECUTION COPY
                try {
                    if (executionInputs[8] === undefined || executionInputs[8] === '') {
                        executionInputs[8] = ethers.constants.AddressZero;
                    } else {
                        // Normalize the address to checksum format
                        executionInputs[8] = ethers.utils.getAddress(executionInputs[8]);
                    }
                    logger.log("refundReceiver address normalized:", executionInputs[8]);
                } catch (error) {
                    logger.error("Invalid refundReceiver address:", executionInputs[8], error);
                    throw new Error(`Invalid refundReceiver address: ${executionInputs[8]}`);
                }

                // signatures (bytes) - This will be collected through the signature flow
                if (executionInputs[9] === undefined || executionInputs[9] === '') {
                    executionInputs[9] = '0x';
                }

                logger.log("🔒 Executing execTransaction with execution copy (UI unchanged):", {
                    to: executionInputs[0],
                    value: executionInputs[1]?.toString(),
                    dataPreview: executionInputs[2]?.substring(0, 50) + '...',
                    operation: executionInputs[3]
                });

                // Clear any existing signature state before starting a new flow
                clearSignatures();

                // Show the signature flow component directly in the Function Explorer tab
                setShowSignatureFlow(true);

                // 🔒 CRITICAL: Store EXECUTION COPY to localStorage (with decrypted data)
                // This is INTERNAL to Function Explorer and must work standalone AND in workflows
                logger.log('🔍 DIAGNOSTIC: About to store to localStorage', {
                    functionName,
                    executionInputsLength: executionInputs.length,
                    executionInputsPreview: executionInputs.map((input, idx) => ({
                        index: idx,
                        type: typeof input,
                        value: typeof input === 'string' ? input.substring(0, 50) + '...' : String(input)
                    }))
                });

                try {
                    const dataToStore = {
                        functionName: functionName,
                        inputs: executionInputs,  // Use execution copy with decrypted data
                        contractAddress: selectedContract?.address,
                        contractName: selectedContract?.name
                    };

                    logger.log('🔍 DIAGNOSTIC: Data object created, attempting JSON.stringify...');
                    const jsonString = JSON.stringify(dataToStore);
                    logger.log('🔍 DIAGNOSTIC: JSON.stringify successful, length:', jsonString.length);

                    localStorage.setItem('function_inputs', jsonString);
                    logger.log('✅ Stored execution copy to localStorage for signature flow');

                    // Verify storage
                    const verification = localStorage.getItem('function_inputs');
                    if (verification) {
                        const parsed = JSON.parse(verification);
                        logger.log('✅ VERIFICATION: localStorage read successful', {
                            functionName: parsed.functionName,
                            inputsLength: parsed.inputs?.length || 0
                        });
                    } else {
                        logger.error('❌ VERIFICATION FAILED: localStorage.getItem returned null');
                    }
                } catch (storageError) {
                    logger.error('❌ CRITICAL: Failed to store to localStorage:', storageError);
                    logger.error('❌ executionInputs at time of error:', executionInputs);
                    throw storageError; // Re-throw to prevent signature flow from starting
                }

                // Workflow tracking is separate and optional
                if (workflowContext) {
                    const workflowKey = workflowContext.registerWorkflow('signature_collection');
                    logger.log('📋 Registered workflow tracking:', workflowKey);
                }

                // Set flag to indicate signature flow is active
                localStorage.setItem('signature_flow_active', 'true');

                // Inform the user
                setExecutionStatus('Signature collection flow initiated. Please collect signatures below.');

                // Return early to prevent direct execution
                return;
            }

            // Call the function
            const tx = await contract[functionName](...inputs);

            setExecutionStatus(`Transaction submitted: ${tx.hash}`);

            // Wait for confirmation
            await tx.wait();

            setExecutionStatus(`Transaction confirmed: ${tx.hash}`);
        } catch (error) {
            logger.error("Error executing function:", error);
            setExecutionStatus(`Error: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    // Workflow integration handlers
    const handleStartWorkflow = (template: WorkflowTemplate) => {
        logger.log('🚀 Starting workflow from Function Explorer:', template.name);

        // Close the shortcuts drawer
        setShowWorkflowShortcuts(false);

        // Navigate to workflows tab and start the selected template
        // This would typically be handled by a parent component or router
        // For now, we'll just log and show a message
        setExecutionStatus(`Starting workflow: ${template.name}. Please navigate to the Workflows tab to continue.`);

        // In a real implementation, this might:
        // 1. Switch to the workflows tab
        // 2. Pre-populate the workflow with current function data
        // 3. Start the workflow execution
    };

    const handleCreateWorkflowFromCurrent = async () => {
        logger.log('💾 Creating workflow from current function');

        if (!selectedContract || !selectedFunction) {
            setExecutionStatus("Please select a contract and function first before creating a workflow.");
            return;
        }

        try {
            setExecutionStatus("Saving workflow template to secure backend...");

            // Get the current function's encoded data if available
            const functionAbi = selectedContract.abi.find(
                (item: any) => item.type === 'function' && item.name === selectedFunction
            );

            let encodedData = '';
            if (functionAbi && functionInputs[selectedFunction]) {
                try {
                    const iface = new ethers.utils.Interface([functionAbi]);
                    encodedData = iface.encodeFunctionData(selectedFunction, functionInputs[selectedFunction]);
                } catch (error) {
                    logger.warn('Could not encode function data:', error);
                }
            }

            // Create a template using the backend storage service
            const templateData: TemplateData = {
                contract: selectedContract.name,
                contractAddress: selectedContract.address,
                functionName: selectedFunction,
                functionData: encodedData,
                inputs: functionInputs[selectedFunction] || [],
                timestamp: Date.now(),
                description: `Function Explorer template for ${selectedContract.name}.${selectedFunction}`,
                tags: ['function-explorer', selectedContract.name.toLowerCase(), selectedFunction]
            };

            const response = await workflowStorageService.storeTemplate(templateData);

            if (response.success) {
                setExecutionStatus(`✅ Workflow template created successfully! ID: ${response.id}`);
                logger.log('Template stored with ID:', response.id);
            } else {
                throw new Error(response.error || 'Failed to store template');
            }
        } catch (error) {
            logger.error('Failed to store workflow template:', error);

            // Fallback to localStorage if backend fails
            try {
                const fallbackData = {
                    contract: selectedContract.name,
                    contractAddress: selectedContract.address,
                    functionName: selectedFunction,
                    functionData: '',
                    inputs: functionInputs[selectedFunction] || [],
                    timestamp: Date.now()
                };

                const savedWorkflows = JSON.parse(localStorage.getItem('customWorkflows') || '[]');
                savedWorkflows.push(fallbackData);
                localStorage.setItem('customWorkflows', JSON.stringify(savedWorkflows));

                setExecutionStatus(`⚠️ Backend unavailable. Template saved locally as fallback.`);
            } catch (fallbackError) {
                setExecutionStatus(`❌ Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }

        setShowWorkflowShortcuts(false);
    };

    // Get current context for workflow suggestions
    const getCurrentContext = () => {
        return {
            contract: selectedContract?.name,
            functionName: selectedFunction || undefined,
            functionData: selectedFunction && functionInputs[selectedFunction] ?
                `Function: ${selectedFunction} with ${functionInputs[selectedFunction].length} inputs` : undefined,
            description: selectedContract && selectedFunction ?
                `${selectedContract.name}.${selectedFunction}` : undefined
        };
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" component="h1">
                        Function Explorer
                    </Typography>
                    {isWorkflowMode && workflowFilterTarget && (
                        <Chip
                            label={`Workflow Mode: ${workflowFilterTarget}`}
                            color="primary"
                            variant="filled"
                            size="small"
                            icon={<WorkflowIcon />}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {executionStatus && (
                        <CircularProgress size={24} />
                    )}
                </Box>
            </Box>

            <Grid2 container spacing={3}>
                {/* Contract Selection Card */}
                <Grid2 size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <ContractIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Contract Selection
                            </Typography>
                        </Box>

                        {contracts.length > 0 ? (
                            <ContractDropdown
                                value={selectedContract?.address || ''}
                                onChange={(address, contract) => {
                                    handleContractSelect(contract || null);
                                }}
                                label=""
                                placeholder="Choose a contract from your ecosystem"
                                helperText="Select a contract to explore its functions"
                                showContractInfo={true}
                                includeCustom={true}
                            />
                        ) : (
                            <Alert severity="info">No contracts available</Alert>
                        )}

                        {selectedContract && (
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={selectedContract.name}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {selectedContract.address}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Functions:</strong> {functions.length}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid2>

                {/* Utility Tools Card */}
                {selectedContract && (
                    selectedFunction === 'schedule' ||
                    selectedFunction === 'execute' ||
                    (selectedContract.name && selectedContract.name.toLowerCase().includes('timelock'))
                ) && (
                        <Grid2 size={{ xs: 12, md: 8 }}>
                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <UtilityIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">
                                        Utility Tools
                                    </Typography>
                                </Box>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <SaltGenerator
                                            contractAddress={selectedContract.address}
                                            functionName={selectedFunction || undefined}
                                            onSaltGenerated={(salt, id) => {
                                                logger.log('🧂 PHASE 1 FIX: Salt generated from SaltGenerator:', { salt, id });

                                                // Update salt for schedule function (parameter index 4)
                                                if (selectedFunction === 'schedule') {
                                                    const saltInputIndex = 4;
                                                    handleInputChange('schedule', salt, saltInputIndex);
                                                    logger.log('✅ PHASE 1 FIX: Salt updated in schedule function inputs');
                                                }

                                                // Update salt for execute function (parameter index 4) 
                                                if (selectedFunction === 'execute') {
                                                    const saltInputIndex = 4;
                                                    handleInputChange('execute', salt, saltInputIndex);
                                                    logger.log('✅ PHASE 1 FIX: Salt updated in execute function inputs');
                                                }
                                            }}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, sm: 6 }}>
                                        <DelayHelper
                                            contractAddress={selectedContract.address}
                                            onDelayFetched={(delay) => {
                                                logger.log('⏰ PHASE 1 FIX: Delay fetched from DelayHelper:', delay);

                                                if (selectedFunction === 'schedule') {
                                                    const delayInputIndex = 5;
                                                    handleInputChange('schedule', delay, delayInputIndex);
                                                    logger.log('✅ PHASE 1 FIX: Delay updated in schedule function inputs');
                                                }
                                            }}
                                        />
                                    </Grid2>
                                </Grid2>
                            </Paper>
                        </Grid2>
                    )}

                {/* Functions Grid */}
                {selectedContract && functions.length > 0 && (
                    <Grid2 size={{ xs: 12 }}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <FunctionsIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    {selectedContract.name} Functions ({functions.length})
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />

                            <Grid2 container spacing={2}>
                                {functions.map((func: any) => (
                                    <Grid2 size={{ xs: 12, sm: 6, lg: 4 }} key={`${func.name}-${func.signature || JSON.stringify(func.inputs)}`}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                height: '100%',
                                                '&:hover': {
                                                    boxShadow: 2,
                                                    borderColor: 'primary.main'
                                                }
                                            }}
                                        >
                                            {/* Function Header */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                {func.stateMutability === 'view' || func.stateMutability === 'pure' ? (
                                                    <ViewIcon color="success" sx={{ mr: 1 }} />
                                                ) : (
                                                    <WriteIcon color="warning" sx={{ mr: 1 }} />
                                                )}
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {func.name}
                                                </Typography>
                                            </Box>

                                            {/* Function Type Chip */}
                                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    {func.stateMutability === 'view' || func.stateMutability === 'pure' ? (
                                                        <Chip
                                                            label={func.inputs && func.inputs.length > 0 ? "Read (inputs required)" : "Read (no inputs)"}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="Write (requires gas)"
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                                {/* Wei Converter - Show for functions with uint256 parameters */}
                                                {func.inputs && func.inputs.some((input: any) => input.type.startsWith('uint')) && (
                                                    <WeiInputConverter
                                                        value=""
                                                        onChange={(value) => {
                                                            const uint256Index = func.inputs.findIndex((input: any) => input.type.startsWith('uint'));
                                                            if (uint256Index !== -1) {
                                                                handleInputChange(func.name, value, uint256Index);
                                                            }
                                                        }}
                                                        label="Amount"
                                                        tokenSymbol="MEMER"
                                                        tokenDecimals={18}
                                                    />
                                                )}
                                            </Box>

                                            {/* Function Inputs */}
                                            {func.inputs && func.inputs.length > 0 ? (
                                                <Box sx={{ mb: 2 }}>
                                                    {func.inputs.map((input: any, idx: number) => {
                                                        const currentValue = functionInputs[func.name]?.[idx] || '';
                                                        const smartDefault = getSmartDefault(
                                                            input.type,
                                                            input.name || `param${idx + 1}`,
                                                            {
                                                                selectedContract,
                                                                functionName: func.name,
                                                                userAddress: undefined, // Will be populated by smart defaults when needed
                                                                isMultiSig: selectedContract?.name?.toLowerCase().includes('multisig'),
                                                                isTimelock: selectedContract?.name?.toLowerCase().includes('timelock')
                                                            }
                                                        );

                                                        const validation = validateParameterValue(
                                                            input.type,
                                                            input.name || `param${idx + 1}`,
                                                            currentValue
                                                        );

                                                        // Override validation for data fields with secure references
                                                        if ((input.name === 'data' || idx === 2) && currentValue && typeof currentValue === 'string' && backendDataHandler.isSecureReference(currentValue)) {
                                                            validation.isValid = true;
                                                            validation.error = undefined;
                                                        }

                                                        // CRITICAL FIX: Don't use smart defaults for KV references - WITH TYPE SAFETY
                                                        const isKVReference = currentValue && typeof currentValue === 'string' && backendDataHandler.isSecureReference(currentValue);
                                                        const safeSmartDefault = isKVReference ? '' : smartDefault;
                                                        const safePlaceholder = isKVReference ? 'Secure workflow reference' : smartDefault;

                                                        // Check if this is an address parameter
                                                        const isAddressParam = input.type === 'address';

                                                        return isAddressParam ? (
                                                            <Box
                                                                key={`${func.name}-${input.name}-${idx}`}
                                                                sx={{ mb: 1 }}
                                                                onFocus={(e) => {
                                                                    // CRITICAL FIX: Auto-fill smart default on focus (same as TextField behavior)
                                                                    // Only auto-fill if field is empty and we have a smart default
                                                                    if (!currentValue && safeSmartDefault && !isKVReference) {
                                                                        handleInputChange(func.name, safeSmartDefault, idx);
                                                                    }
                                                                }}
                                                            >
                                                                <AddressSelector
                                                                    value={currentValue}
                                                                    onChange={(value) => {
                                                                        // User explicitly changed the value (either from dropdown or manual input)
                                                                        // This will replace any auto-filled default
                                                                        handleInputChange(func.name, value, idx);
                                                                    }}
                                                                    contracts={contracts}
                                                                    protocolAddresses={protocolAddresses}
                                                                    label={`${input.name || `param${idx + 1}`} (${input.type})`}
                                                                    placeholder={safeSmartDefault || "Select contract or enter address"}
                                                                    allowManualInput={true}
                                                                    error={currentValue !== '' && !validation.isValid}
                                                                    helperText={
                                                                        isKVReference
                                                                            ? '🔒 Secure workflow reference - will be decrypted at execution'
                                                                            : currentValue !== '' && !validation.isValid
                                                                                ? validation.error
                                                                                : safeSmartDefault
                                                                                    ? `Default: ${safeSmartDefault.length > 20 ? safeSmartDefault.substring(0, 20) + '...' : safeSmartDefault}`
                                                                                    : 'Select from Memer ecosystem, protocol addresses, or enter custom address'
                                                                    }
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <TextField
                                                                key={`${func.name}-${input.name}-${idx}`}
                                                                label={`${input.name || `param${idx + 1}`} (${input.type})`}
                                                                value={currentValue}
                                                                placeholder={safePlaceholder}
                                                                onChange={(e) => handleInputChange(func.name, e.target.value, idx)}
                                                                onFocus={(e) => {
                                                                    // CRITICAL FIX: Don't auto-fill KV references with smart defaults
                                                                    if (!e.target.value && safeSmartDefault && !isKVReference) {
                                                                        handleInputChange(func.name, safeSmartDefault, idx);
                                                                    }
                                                                }}
                                                                fullWidth
                                                                margin="dense"
                                                                size="small"
                                                                variant="outlined"
                                                                error={currentValue !== '' && !validation.isValid}
                                                                helperText={
                                                                    isKVReference
                                                                        ? '🔒 Secure workflow reference - will be decrypted at execution'
                                                                        : currentValue !== '' && !validation.isValid
                                                                            ? validation.error
                                                                            : safeSmartDefault
                                                                                ? `Default: ${safeSmartDefault.length > 20 ? safeSmartDefault.substring(0, 20) + '...' : safeSmartDefault}`
                                                                                : undefined
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                                    No inputs required
                                                </Typography>
                                            )}

                                            {/* Execute Button */}
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    startIcon={<ExecuteIcon />}
                                                    onClick={() =>
                                                        func.stateMutability === 'view' || func.stateMutability === 'pure'
                                                            ? executeReadFunction(func.name)
                                                            : executeWriteFunction(func.name)
                                                    }
                                                    disabled={
                                                        !provider ||
                                                        (func.stateMutability !== 'view' && func.stateMutability !== 'pure' && !signer) ||
                                                        (showSignatureFlow && (func.name === 'execTransaction' || func.name === 'executeTransactionWithChecks'))
                                                    }
                                                    color={func.stateMutability === 'view' || func.stateMutability === 'pure' ? 'success' : 'warning'}
                                                >
                                                    {showSignatureFlow && (func.name === 'execTransaction' || func.name === 'executeTransactionWithChecks')
                                                        ? 'Signature Flow Active'
                                                        : results[func.name] ? 'Refresh' : 'Execute'
                                                    }
                                                </Button>
                                                {results[func.name] && (
                                                    <Tooltip title="Clear result">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                const updatedResults = { ...results };
                                                                delete updatedResults[func.name];
                                                                setResults(updatedResults);
                                                                logger.log(`🧹 Cleared result for ${func.name}`);
                                                            }}
                                                            sx={{
                                                                bgcolor: 'action.hover',
                                                                '&:hover': { bgcolor: 'action.selected' }
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>

                                            {/* Result Display */}
                                            {results[func.name] && (
                                                <Box sx={{ mt: 2 }}>
                                                    {/* 🎯 PHASE 1: Show BurstDataDisplay for burst functions */}
                                                    {parsedResults[func.name]?.displayType === 'burst' && parsedResults[func.name]?.structured ? (
                                                        <BurstDataDisplay
                                                            data={parsedResults[func.name].structured}
                                                            compact={false}
                                                        />
                                                    ) : (
                                                        /* Standard result display for non-burst functions */
                                                        <Box sx={{
                                                            p: 2,
                                                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                            borderRadius: 1,
                                                            border: '1px solid rgba(76, 175, 80, 0.3)'
                                                        }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                Result:
                                                            </Typography>
                                                            <SmartHexDisplay
                                                                value={results[func.name]}
                                                                label={`${func.name} result`}
                                                                functionName={func.name}
                                                                contractContext={{
                                                                    name: selectedContract?.name,
                                                                    symbol: selectedContract?.name?.includes('MEMER') ? 'MEMER' : undefined,
                                                                    decimals: 18
                                                                }}
                                                                showInlineConversion={true}
                                                                compactConversion={true}
                                                                onOpenFloatingConverter={openConverter}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Paper>
                                    </Grid2>
                                ))}
                            </Grid2>
                        </Paper>
                    </Grid2>
                )}

                {/* Execution Status */}
                {executionStatus && (
                    <Grid2 size={{ xs: 12 }}>
                        {executionStatus.toLowerCase().includes('error') ? (
                            <ErrorDisplay
                                error={executionStatus}
                                context="Function Execution"
                                severity="error"
                                onDismiss={() => setExecutionStatus('')}
                            />
                        ) : (
                            <Alert
                                severity={
                                    executionStatus.toLowerCase().includes('success') ? 'success' : 'info'
                                }
                                onClose={() => setExecutionStatus('')}
                            >
                                {executionStatus}
                            </Alert>
                        )}
                    </Grid2>
                )}

                {/* Decrypt Function Data Section */}
                <Grid2 size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <SecurityIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Decrypt Function Data
                            </Typography>
                        </Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Paste encrypted function data from the Transaction Builder to decrypt and analyze it here.
                        </Alert>

                        <TextField
                            label="Encrypted Function Data"
                            placeholder="Paste encrypted function data here (e.g., ENCRYPTED:U2FsdGVkX1...)"
                            multiline
                            rows={3}
                            fullWidth
                            value={encryptedFunctionData}
                            onChange={(e) => setEncryptedFunctionData(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        {encryptedFunctionData && encryptedFunctionData.startsWith('ENCRYPTED:') && (
                            <SecureDataViewer
                                data={encryptedFunctionData}
                                title="Encrypted Function Data"
                                description="This encrypted data contains a function call that can be analyzed and used in transactions."
                                contracts={contracts}
                                onCopy={() => {
                                    logger.log('Function data copied to clipboard');
                                }}
                            />
                        )}

                        {encryptedFunctionData && !encryptedFunctionData.startsWith('ENCRYPTED:') && encryptedFunctionData.startsWith('0x') && (
                            <SecureDataViewer
                                data={encryptedFunctionData}
                                title="Raw Function Data"
                                description="This appears to be raw hex function data."
                                contracts={contracts}
                                onCopy={() => {
                                    logger.log('Function data copied to clipboard');
                                }}
                            />
                        )}
                    </Paper>
                </Grid2>

                {/* Signature Flow */}
                {showSignatureFlow && selectedContract && signer && (
                    <Grid2 size={{ xs: 12 }}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SignatureIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Signature Collection
                                </Typography>
                            </Box>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Please collect signatures from the required owners to execute this transaction.
                            </Alert>
                            <MultiSigWalletSignatureFlow
                                selectedContract={selectedContract}
                                signer={signer}
                                chainId={provider ? (provider as any).network?.chainId?.toString() || '1' : '1'}
                                functionInputs={functionInputs}
                                owners={owners}
                                onSignaturesCollected={async (signatures) => {
                                    logger.log("🎯 FunctionExplorer: Signatures collected, executing transaction directly");
                                    logger.log("Signatures collected:", signatures);
                                    setShowSignatureFlow(false);
                                    setExecutionStatus("Signatures collected successfully. Executing transaction...");

                                    try {
                                        // ACTUAL TRANSACTION EXECUTION - STANDALONE MODE
                                        logger.log("🚀 FunctionExplorer: Executing MultiSig transaction on blockchain (standalone mode)...");

                                        if (!signer || !selectedContract) {
                                            throw new Error('No signer or contract available');
                                        }

                                        // Get the function name (execTransaction or executeTransactionWithChecks)
                                        const functionName = selectedFunction || 'execTransaction';

                                        // 🔒 CRITICAL FIX: Read from localStorage FIRST (execution-ready data), NOT React state
                                        // React state has KV references, localStorage has decrypted hex
                                        const storedData = localStorage.getItem('function_inputs');
                                        const inputs = storedData ? JSON.parse(storedData).inputs : functionInputs[functionName] || [];

                                        // NOW clean up localStorage and sessionStorage AFTER reading the data
                                        sessionStorage.removeItem('workflowAutoSelection');
                                        sessionStorage.removeItem('workflowStepData');
                                        localStorage.removeItem('signature_flow_active');
                                        localStorage.removeItem('function_inputs');

                                        logger.log("🔍 FunctionExplorer: Using inputs from localStorage (execution-ready):", {
                                            functionName,
                                            inputsLength: inputs.length,
                                            to: inputs[0],
                                            value: inputs[1],
                                            dataPreview: inputs[2]?.substring(0, 20) + '...',
                                            operation: inputs[3]
                                        });

                                        // Signatures are already sorted by MultiSigWalletSignatureFlow
                                        // Just concatenate them in the order received
                                        logger.log("📝 CONCATENATION DEBUG:", {
                                            signaturesReceived: signatures.map(s => ({
                                                address: s.signer,
                                                sigPrefix: s.signature.substring(0, 20) + '...',
                                                sigLength: s.signature.length
                                            }))
                                        });

                                        // Concatenate the signatures (already sorted by MultiSigWalletSignatureFlow)
                                        let concatenatedSignatures = "0x";
                                        for (const sig of signatures) {
                                            // Remove '0x' prefix from all signatures
                                            concatenatedSignatures += sig.signature.slice(2);
                                        }
                                        const finalSignatures = concatenatedSignatures;

                                        logger.log("🔗 FINAL CONCATENATED SIGNATURES:", {
                                            finalLength: finalSignatures.length,
                                            finalPrefix: finalSignatures.substring(0, 50) + '...',
                                            signatureCount: signatures.length,
                                            expectedLength: 2 + (signatures.length * 130), // 0x + (65 bytes * 2 chars/byte * count)
                                            addressOrder: signatures.map(s => s.signer)
                                        });

                                        // Update the signatures in the inputs array (last parameter)
                                        const finalInputs = [...inputs];
                                        finalInputs[9] = finalSignatures; // signatures parameter

                                        logger.log("📤 FunctionExplorer: Submitting transaction with signatures:", {
                                            functionName,
                                            contractAddress: selectedContract.address,
                                            signaturesCount: signatures.length,
                                            finalSignatures: finalSignatures.substring(0, 20) + '...',
                                            finalInputsLength: finalInputs.length
                                        });

                                        // Create contract instance and execute
                                        const contract = new ethers.Contract(
                                            selectedContract.address,
                                            selectedContract.abi,
                                            signer
                                        );

                                        // CRITICAL: Execute the MultiSig transaction directly
                                        logger.log("🎯 FunctionExplorer: Calling contract function directly...");
                                        const tx = await contract[functionName](...finalInputs);

                                        logger.log('⏳ FunctionExplorer: Transaction submitted, waiting for confirmation...', tx.hash);
                                        setExecutionStatus(`Transaction submitted: ${tx.hash}. Waiting for confirmation...`);

                                        // Wait for transaction confirmation
                                        const receipt = await tx.wait();

                                        logger.log('✅ FunctionExplorer: Transaction confirmed!', {
                                            hash: tx.hash,
                                            blockNumber: receipt.blockNumber,
                                            gasUsed: receipt.gasUsed.toString()
                                        });

                                        setExecutionStatus(`✅ Transaction executed successfully! TX: ${tx.hash}`);

                                        // Update results to show success
                                        setResults({
                                            ...results,
                                            [functionName]: `Transaction executed: ${tx.hash}`
                                        });

                                        // WORKFLOW INTEGRATION: Notify workflow of transaction completion
                                        if (onTransactionComplete) {
                                            logger.log('🔗 Notifying workflow of transaction completion...');

                                            const executionContext = {
                                                functionName,
                                                contractAddress: selectedContract.address,
                                                contractName: selectedContract.name,
                                                inputs: finalInputs,
                                                signatures: finalSignatures,
                                                signaturesCount: signatures.length
                                            };

                                            try {
                                                onTransactionComplete(tx.hash, receipt, executionContext);
                                                logger.log('✅ Workflow notified of transaction completion');
                                            } catch (callbackError) {
                                                logger.error('❌ Error notifying workflow:', callbackError);
                                                // Don't fail the transaction if callback fails
                                            }
                                        }

                                    } catch (error) {
                                        // Use secure error logging to prevent hex data exposure
                                        logger.secureError(error, 'MultiSig Transaction Execution', {
                                            preserveAddresses: true,
                                            preserveErrorCodes: true,
                                            logFullErrorServerSide: true
                                        });

                                        // Create user-friendly error message for UI
                                        const userFriendlyMessage = logger.createUserFriendlyErrorMessage(error, 'MultiSig Transaction Execution');
                                        setExecutionStatus(userFriendlyMessage);

                                        // Get debugging suggestions
                                        const suggestions = logger.getErrorSuggestions(error, 'MultiSig Transaction Execution');
                                        if (suggestions.length > 0) {
                                            logger.info('💡 Debugging suggestions:', suggestions);
                                        }
                                    }
                                }}
                                onSignerChanged={(newSigner) => {
                                    logger.log("Signer changed:", newSigner);
                                }}
                            />
                        </Paper>
                    </Grid2>
                )}
            </Grid2>

            {/* Workflow Shortcuts Floating Action Button */}
            <Fab
                color="primary"
                aria-label="workflow shortcuts"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000
                }}
                onClick={() => setShowWorkflowShortcuts(true)}
            >
                <WorkflowIcon />
            </Fab>

            {/* Workflow Shortcuts Drawer */}
            <Drawer
                anchor="right"
                open={showWorkflowShortcuts}
                onClose={() => setShowWorkflowShortcuts(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 400,
                        maxWidth: '90vw'
                    }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        Workflow Shortcuts
                    </Typography>
                    <IconButton onClick={() => setShowWorkflowShortcuts(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
                    <WorkflowShortcuts
                        onStartWorkflow={handleStartWorkflow}
                        onCreateWorkflowFromCurrent={handleCreateWorkflowFromCurrent}
                        currentContext={getCurrentContext()}
                        compact={false}
                    />
                </Box>
            </Drawer>
        </Box>
    );
};

export default FunctionExplorer;
